import { Button } from "armstrong-react";
import classNames from "classnames";
import React from "react";
import { connect } from "react-redux";
import _ from "underscore";
import { ActionPromises } from "../../portal/state";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { ShowMoreHeightFade } from "@travellocal/ui";
import { Loader } from "../atoms";
import PagerScroll from "./pagerScroll/pagerScroll";
import { SnippetsApiParams } from "../../shared/api/system/apiParameters";

import "./snippetPicker.scss";

interface OwnProps {
  insertSnippet: (snippetContent: string) => void;
  onInsert?: () => void;
}

interface LocalState {
  snippetContent: string;
  selectedSnippetId: number;
  loadingSnippet: boolean;
  error: any;
}

interface StateProps {
  snippets: IPagerState<ISnippet>;
  enquiry: IState<IEnquiryOutput>;
  lto: IState<ILto>;
}

interface DispatchProps {
  recordSnippetUsage: (enquiryId: number, snippetId: number) => void;
  getSnippetsPage: (index: number, params?: SnippetsApiParams) => Promise<any>;
  getSingleSnippet: (id: number) => Promise<ISnippet>;
}

type Props = OwnProps & StateProps & DispatchProps;

class SnippetPicker extends React.Component<Props, LocalState> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      selectedSnippetId: null,
      snippetContent: null,
      loadingSnippet: false,
      error: null,
    };
  }

  public componentDidMount() {
    const ltoParam: SnippetsApiParams = this.props.lto.data != null ? {
      ltoId: this.props.lto.data.id,
    } : {};

    if (this.props.snippets.data == null || this.props.snippets.data.length === 0) {
      this.props.getSnippetsPage(1, ltoParam);
    }
  }

  /**
   * Handle snippet selection
   * @param {number} selectedSnippetIndex - array position of the selected snippet
   * @return void
   */
  private selectSnippet = (snippet: ISnippet): void => {
    GoogleAnalyticsHelper.logEvent("Message snippets", "Click", "Select snippet");

    this.setState({
      selectedSnippetId: snippet.id,
      snippetContent: snippet.contentSummary ?? snippet.content,
    });
  }

  /**
   * Build a jsx list item for a snippet title
   * @param {ISnippetMeta} snippet - a snippet object
   * @param {number} index - array position of snippet
   * @return JSX.Element
   */
  private renderTitle = (snippet: ISnippet, index: number): JSX.Element => {
    return (
      <li
        key={index}
        className={classNames({ "snippet-picker__selected": snippet.id === this.state.selectedSnippetId })}
        onClick={() => this.selectSnippet(snippet)}
      >
        {snippet.title}
      </li>
    );
  }

  private insertHandler = async () => {
    const { recordSnippetUsage, insertSnippet, enquiry, snippets } = this.props;
    const { selectedSnippetId } = this.state;
    const enquiryId: number = enquiry != null && enquiry.data != null && enquiry.data.id;
    const currentSnippetContent = snippets.data.find(x => x.id === selectedSnippetId).content;

    insertSnippet(currentSnippetContent);

    if (enquiryId) {
      setTimeout(() => {
        recordSnippetUsage(enquiryId, selectedSnippetId);
      }, 500);
    }

    if (this.props.onInsert) {
      this.props.onInsert();
    }
  }

  private getSingleSnippet = async (id: number): Promise<void> => {
    if (this.hasSnippetLoaded(id)) {
      return;
    }

    this.setState({ loadingSnippet: true });
    try {
      await this.props.getSingleSnippet(id);
    }
    catch (e) {
      this.setState({ error: e.message });
    }
    finally {
      this.setState({ loadingSnippet: false });
    }
  }

  private hasSnippetLoaded = (id: number): boolean => {
    return this.props.snippets.data.find(x => x.id === id).content != null
  }

  public render() {
    const { snippets } = this.props;
    const { snippetContent, selectedSnippetId, loadingSnippet } = this.state;
    const ltoParam: SnippetsApiParams = this.props.lto.data != null ? {
      ltoId: this.props.lto.data.id,
    } : {};

    return (
      <>
        {snippets.data != null && (
          <div className={classNames("snippet-picker", {
            "no-content": snippets.data.length === 0,
          })}>
            <PagerScroll className="snippet-picker__list" items={snippets} getPage={(page) => this.props.getSnippetsPage(page, ltoParam)}>
              <ul>
                {snippets.data.length > 0
                  ? snippets.data.map((snippet, i) => this.renderTitle(snippet, i))
                  : snippets.status === "OK" && <p>There are no snippets available</p>
                }
              </ul>
            </PagerScroll>
          </div>
        )}

        {snippetContent &&
          <div className="snippet-picker__preview">
            <div className="snippet-picker__content">
              {this.hasSnippetLoaded(selectedSnippetId) ?
                <div dangerouslySetInnerHTML={{ __html: loadingSnippet ? "" : snippets.data.find(x => x.id === selectedSnippetId).content }} />
                :
                <ShowMoreHeightFade
                  className="snippet-picker__fade"
                  threshold={55}
                  hideShowLess
                  key={selectedSnippetId} // need key to force rerender when snippet id changes
                  onOpen={() => this.getSingleSnippet(selectedSnippetId)}
                >
                  <div
                    style={{ marginBottom: "55px" }} // force show more button to always appear
                    dangerouslySetInnerHTML={{ __html: loadingSnippet ? "" : snippets.data.find(x => x.id === selectedSnippetId).contentSummary + "…"}}
                  />
                </ShowMoreHeightFade>
              }
              {loadingSnippet && <Loader />}
            </div>
            <div className="snippet-picker__insert">
              {this.hasSnippetLoaded(selectedSnippetId) &&
                <Button
                  type="button"
                  rightIcon="icon-download4"
                  onClick={this.insertHandler}
                >
                  Insert
              </Button>
              }
            </div>
          </div>
        }
      </>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  recordSnippetUsage: (enquiryId: number, snippetId: number) => dispatch(ActionPromises.saveDraftSnippet(enquiryId, snippetId)),
  getSnippetsPage: (index: number, params?: SnippetsApiParams) => dispatch(ActionPromises.getSnippetsPage(index, 10, { sort: SnippetSort.TitleAsc, statuses: [SnippetStatus.Live], ...params })),
  getSingleSnippet: (id: number) => dispatch(ActionPromises.getSnippetById(id)),
});

const mapStateToProps = (state: IAppStoreState): StateProps => ({
  snippets: state.snippetsPages,
  enquiry: state.enquiry || null,
  lto: state.lto,
});

export default connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(SnippetPicker);
